import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Box, Button, Group, Slider, Text, rem } from "@mantine/core";
import { IconDeviceFloppy, IconX, IconZoomIn } from "@tabler/icons-react";

const ASPECT = 9 / 16;

// Renders a single Image at the chosen crop, returns a JPEG blob. The pixel
// region (areaPixels) comes from react-easy-crop's onCropComplete; we feed it
// into a canvas at native resolution so no quality is lost on the way out.
async function cropToBlob(imageSrc, areaPixels) {
  const image = await new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(areaPixels.width);
  canvas.height = Math.round(areaPixels.height);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    areaPixels.x,
    areaPixels.y,
    areaPixels.width,
    areaPixels.height,
    0,
    0,
    canvas.width,
    canvas.height
  );

  return await new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92);
  });
}

export default function CropForVideo({ imageUri, onCancel, onSave, busy }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);

  const onCropComplete = useCallback((_areaPercent, pixels) => {
    setAreaPixels(pixels);
  }, []);

  const handleSave = async () => {
    if (!areaPixels) return;
    const blob = await cropToBlob(imageUri, areaPixels);
    if (blob) onSave(blob);
  };

  return (
    <Box>
      <Box
        // The cropper needs a sized container. dvh keeps it stable when the
        // mobile URL bar collapses; 56vh is roughly half the screen so the
        // action buttons stay above the fold on phones.
        style={{
          position: "relative",
          width: "100%",
          height: "min(70dvh, 640px)",
          background: "#000",
          borderRadius: rem(8),
          overflow: "hidden",
          touchAction: "none",
        }}
      >
        <Cropper
          image={imageUri}
          crop={crop}
          zoom={zoom}
          aspect={ASPECT}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          restrictPosition
          showGrid
        />
      </Box>

      <Group mt="sm" gap="sm" wrap="nowrap" align="center">
        <IconZoomIn size={16} stroke={1.5} />
        <Slider
          value={zoom}
          onChange={setZoom}
          min={1}
          max={4}
          step={0.05}
          label={null}
          style={{ flex: 1 }}
        />
        <Text c="dimmed" size="xs" w={40} ta="right">
          {zoom.toFixed(1)}×
        </Text>
      </Group>

      <Group mt="sm" gap="xs" justify="flex-end" wrap="wrap">
        <Button
          variant="default"
          size="xs"
          onClick={onCancel}
          leftSection={<IconX size={16} stroke={1.5} />}
          disabled={busy}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          onClick={handleSave}
          loading={busy}
          leftSection={<IconDeviceFloppy size={16} stroke={1.5} />}
          disabled={!areaPixels}
        >
          Save crop
        </Button>
      </Group>
    </Box>
  );
}
